//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { Checkbox, CheckboxTypes } from 'common/components/core/checkbox';
import classnames from 'classnames';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import * as Immutable from 'immutable';

import OutletActions from 'common/redux/actions/outlet';
import SalesCategoriesActions from 'common/redux/actions/salesCategories';

import ToastActions from 'common/redux/actions/toast';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import OutletLocationActions from 'common/redux/actions/outletLocation';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import OutletSelectors from 'common/redux/selectors/outlet';
import SkillSelectors from 'common/redux/selectors/skill';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import LocationSlotsSummary from 'common/entity/components/LocationSlotSummary';
import OutletLocationTypeFilter from 'common/entity/components/OutletLocationTypeFilter';
import MandatoryDaysSummary from 'common/entity/components/MandatoryDaysSummary';
import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import { withRouter } from 'common/components/core/router';
import SingleOutletEditContainer from 'common/entity/outletMaster/SingleOutletEditContainer';
import OutletMasterImportContainer from 'common/entity/outletMaster/OutletMasterImportContainer';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import GeocodeConfidenceFilter from 'common/entity/components/GeocodeConfidenceFilter';
import GeocodeSourceFilter from 'common/entity/components/GeocodeSourceFilter';
import { ProviderEnum } from 'common/enums/GeometryEnum';

import BulkGeocodeEditContainer from 'common/entity/locationMaster/BulkGeocodeEditContainer';
import {
    ConnectedTeamSelectorFilter,
    ConnectedTeamIdSelectorFilter
} from 'common/entity/components/TeamSelectorFilter';
import SlotFilter from 'common/entity/components/SlotFilter';
import GeneralFilter from 'common/entity/components/GeneralFilter';
import {
    ConnectedSalesCategoryFilter,
    ConnectedSalesCategoryIdFilter
} from 'common/entity/components/SalesCategoryFilter';

import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import CustomColumnHelpers from 'common/entity/helpers/CustomColumnHelpers';

import { dashboardColors } from 'common/enums/ColorEnum';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';

import theme from 'common/entity/BulkEditContainer.scss';

import TeamList from 'common/components/core/common/TeamList';
import VerifyButton from 'common/entity/generic/VerifyButton';
import HolidaySelector from 'common/entity/generic/HolidaySelector';
import { DivWithPopover } from 'common/components/core/wrappers/Div';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';

import type { Skill } from 'common/models/internal/Skill';
import type { RouterHistory } from 'react-router-dom';
import type { Location } from 'common/components/core/router';
import type { MasterOutlet } from 'common/models/internal/MasterOutlet';
import type { MasterLocation } from 'common/models/internal/MasterLocation';
import { GeocodingSourceType } from 'common/models/internal/MasterLocation';
import type { Team } from 'common/models/internal/Team';
import type { Map } from 'immutable';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { ToastType } from 'common/components/core/toast/Toast';

import { memoizedImmutable } from 'common/utils/Memoizer';
import { LocationWithMetadata } from 'common/models/internal/LocationWithMetadata';
import {
    FilterBuilder,
    FilterOperation,
    LogicalOperation
} from 'common/models/internal/Filter';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import ChannelPreferenceSelectorFilter from 'common/entity/components/ChannelPreferenceSelectorFilter';
import VisitUtils from 'common/utils/VisitUtils';
import type { EntityColumnType } from 'common/entity/generic/MasterContainer';
import { OutletLocationTypeEnum } from 'common/models/internal/MasterOutlet';
import {
    ENTITY_TABLE_ID,
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID
} from 'common/constants/entity/outletMaster';
import { RoleOperationEnum } from 'common/modules/rbac';
import { MAX_DURATION_FREQUENCY_IN_WEEKS } from './tableConfig';

export const getWeekValues = t => {
    return Array(MAX_DURATION_FREQUENCY_IN_WEEKS)
        .fill(1)
        .map((_, index) => ({
            label: t('outletTable.week', {
                count: index + 1
            }),
            value: 7 * (index + 1)
        }));
};

const selectTeamId = ({ teamId }) => teamId;

const VerifiedButton = ({ location }) => {
    const isVerified = location?.isVerified();
    return <VerifyButton nonEditable theme={theme} isVerified={isVerified} />;
};

const getTeamIdFilter = ({ clientId, filteredColumn }) => {
    const {
        value: { allowEmptyOrNull, values }
    } = filteredColumn;
    const teamValueFilter = values.map(team => ({
        clientId,
        teamId: team
    }));

    return new FilterBuilder()
        .withName('teams')
        .withOperation(FilterOperation.Contains)
        .withValues(teamValueFilter)
        .withAllowEmptyOrNull(allowEmptyOrNull)
        .withCaseSensitive(true);
};

const getSalesCategoryFilter = ({ filteredColumn }) => {
    const {
        value: { values, allowEmptyOrNull }
    } = filteredColumn;
    return new FilterBuilder()
        .withName('salesCategoryId.salesCategoryId')
        .withOperation(FilterOperation.Equals)
        .withValues(values)
        .withAllowEmptyOrNull(allowEmptyOrNull);
};

export const DEFAULT_COLUMNS: Array<EntityColumnType> = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick, theme, disabled }
                }
            } = props;
            return (
                <Checkbox
                    checkboxType={CheckboxTypes.Solid}
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    theme={theme}
                    className={theme.selectionCell}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                    disabled={disabled}
                />
            );
        },
        sortable: false,
        filterable: false,
        width: 40,
        nonModifiable: true,
        frozen: true,
        disablePinControl: true
    },
    {
        headerTextId: 'outletTable.id',
        accessor: 'id',
        minWidth: 100,
        infixSearchable: true,
        frozen: true
    },
    {
        headerTextId: 'outletTable.code',
        id: 'code',
        accessor: 'code',
        minWidth: 100,
        infixSearchable: true,
        show: false,
        frozen: true
    },
    {
        headerTextId: 'outletTable.name',
        id: 'name',
        accessor: 'name',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'outletTable.teamName',
        id: 'teams.name',
        exportId: 'teams.name',
        accessor: 'teams',
        sortable: false,
        minWidth: 200,
        Filter: ({ filter, onChange }) => {
            return (
                <ConnectedTeamSelectorFilter
                    className={theme.filterTextBox}
                    theme={theme}
                    value={filter ? filter.value : null}
                    onChange={onChange}
                />
            );
        },
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = (original: MasterLocation).teams;
            return <TeamList teams={teams} teamIds={teamIds} theme={theme} />;
        }
    },
    {
        headerTextId: 'outletTable.teamId',
        id: 'teams.id',
        exportId: 'teams.id',
        accessor: 'teams',
        sortable: false,
        minWidth: 200,
        Filter: ({ filter, onChange }) => {
            return (
                <ConnectedTeamIdSelectorFilter
                    className={theme.filterTextBox}
                    theme={theme}
                    value={filter ? filter.value : null}
                    onChange={onChange}
                />
            );
        },
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = (original: MasterLocation).teams;
            return (
                <TeamList
                    teams={teams}
                    teamIds={teamIds}
                    theme={theme}
                    selectTeamName={selectTeamId}
                />
            );
        }
    },
    {
        headerTextId: 'outletTable.locationId',
        accessor: 'locationInfo.location.id',
        exportId: 'locationInfo.locationId.locationId',
        minWidth: 130
    },
    {
        headerTextId: 'outletTable.address.completePostalAddress',
        accessor: 'locationInfo.location.address.formattedAddress',
        minWidth: 300,
        infixSearchable: true
    },
    {
        headerTextId: 'outletTable.address.locality',
        accessor: 'locationInfo.location.address.localityName',
        minWidth: 150
    },
    {
        headerTextId: 'outletTable.address.city',
        accessor: 'locationInfo.location.address.city',
        minWidth: 100
    },
    {
        headerTextId: 'outletTable.address.state',
        accessor: 'locationInfo.location.address.state',
        minWidth: 100
    },
    {
        headerTextId: 'outletTable.address.pincode',
        accessor: 'locationInfo.location.address.pincode',
        filterable: false,
        minWidth: 100
    },
    {
        headerTextId: 'outletTable.address.country',
        accessor: 'locationInfo.location.address.countryCode',
        minWidth: 100
    },
    {
        headerTextId: 'outletTable.latitude',
        accessor: 'locationInfo.location.latLng.lat',
        sortable: false,
        filterable: false,
        minWidth: 150
    },
    {
        headerTextId: 'outletTable.longitude',
        accessor: 'locationInfo.location.latLng.lng',
        sortable: false,
        filterable: false,
        minWidth: 150
    },
    {
        headerTextId: 'outletTable.locationType',
        id: 'locationType',
        accessor: 'locationInfo.location.type',
        Cell: props => {
            const {
                value,
                tdProps: {
                    rest: { t, theme }
                }
            } = props;
            const cellText = value
                ? t(`masterOutletProperties.outletLocationTypes.${value}`)
                : '';
            return (
                <div title={cellText} className={theme.cellText}>
                    {cellText}
                </div>
            );
        },
        FilterComponent: OutletLocationTypeFilter,
        getFilterOptions: () => {
            const options = OutletLocationTypeEnum.getOptions({
                translationKey: 'masterOutletProperties.outletLocationTypes'
            });
            return options;
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('locationInfo.location.type')
                .withOperation(FilterOperation.Equals)
                .withValues(values);
        },
        property: 'type',
        minWidth: 100
    },
    {
        headerTextId: 'outletTable.confidence',
        id: 'confidence',
        accessor: 'locationInfo.location.geocodingMetadata.confidence',
        filterable: true,
        sortable: true,
        minWidth: 100,
        FilterComponent: GeocodeConfidenceFilter,
        Cell: props => {
            const {
                original: {
                    locationInfo: { location }
                },
                tdProps: {
                    rest: { t }
                }
            } = props;
            const confidenceLevel =
                location?.geocodingMetadata?.confidenceLevel;
            if (!confidenceLevel) {
                return null;
            }
            return t(
                `masterLocationProperties.confidenceLevels.${confidenceLevel}`
            );
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('locationInfo.location.geocodingMetadata.confidence')
                .withOperation(FilterOperation.Equals)
                .withValues(values);
        }
    },
    {
        headerTextId: 'outletTable.geocodingSource',
        id: 'geoSource',
        fieldId: 'geocodingMetadata.provider',
        accessor: 'locationInfo.location.geocodingMetadata.provider',
        Cell: props => {
            const {
                original: {
                    locationInfo: { location }
                },
                tdProps: {
                    rest: { t }
                }
            } = props;
            const provider = location?.geocodingMetadata?.provider;
            if (!provider) {
                return null;
            }
            return t(
                `masterLocationProperties.geocodingSourcesTypes.${provider}`
            );
        },
        filterable: true,
        sortable: true,
        minWidth: 100,
        FilterComponent: GeocodeSourceFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values }
            } = filteredColumn;
            const sourceValues = [];
            if (values.includes(GeocodingSourceType.Client)) {
                sourceValues.push(
                    ...[ProviderEnum.ClientRead, ProviderEnum.ClientGeocoder]
                );
            }
            if (values.includes(GeocodingSourceType.Locus)) {
                sourceValues.push(ProviderEnum.LocusGeocoder);
            }
            if (values.includes(GeocodingSourceType.SystemLearnt)) {
                sourceValues.push(ProviderEnum.SystemLearnt);
            }
            return new FilterBuilder()
                .withName('locationInfo.location.geocodingMetadata.provider')
                .withOperation(FilterOperation.Equals)
                .withValues(sourceValues)
                .withLogicalOperation(LogicalOperation.OR);
        }
    },
    {
        headerTextId: 'outletTable.channels',
        id: 'channels',
        accessor: 'channels',
        Cell: props => (
            <span>{props?.value?.map(channel => channel.name).join(', ')}</span>
        ),
        sortable: false,
        FilterComponent: ChannelPreferenceSelectorFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values: channels, allowEmptyOrNull }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('channels')
                .withOperation(FilterOperation.Contains)
                .withValues(channels)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: ({ column }) => {
            const { entitySettings } = column.getHeaderProps();
            return entitySettings.pjpSettings?.salesChannels?.toJS();
        }
    },
    {
        headerTextId: 'outletTable.salesCategoryId',
        accessor: 'salesCategory.id',
        FilterComponent: ConnectedSalesCategoryIdFilter,
        getCustomFilterInfo: getSalesCategoryFilter,
        getFilterOptions: ({ column }) => {
            const { entitySettings } = column.getHeaderProps();
            return CustomColumnHelpers.getOptionsByKeyName(
                entitySettings,
                'outletSettings',
                'salesCategory'
            );
        },
        minWidth: 110
    },
    {
        headerTextId: 'outletTable.salesCategoryName',
        accessor: 'salesCategory.name',
        FilterComponent: ConnectedSalesCategoryFilter,
        getCustomFilterInfo: getSalesCategoryFilter,
        getFilterOptions: ({ column }) => {
            const { entitySettings } = column.getHeaderProps();
            return CustomColumnHelpers.getOptionsByKeyName(
                entitySettings,
                'outletSettings',
                'salesCategory'
            );
        },
        minWidth: 110
    },
    {
        headerTextId: 'outletTable.slots',
        id: 'timeSlots',
        property: 'timeSlots',
        sortable: false,
        minWidth: 150,
        Filter: ({ filter, onChange }) => {
            return (
                <SlotFilter
                    className={theme.filterTextBox}
                    theme={theme}
                    value={filter ? filter.value : null}
                    onChange={onChange}
                />
            );
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values, allowEmptyOrNull }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('timeSlots')
                .withOperation(FilterOperation.Contains)
                .withAllowEmptyOrNull(allowEmptyOrNull)
                .withValues(
                    values.map(dayOfWeek => {
                        return {
                            dayOfWeek
                        };
                    })
                );
        },
        accessor: (data: MasterOutlet) => {
            const { timeSlots } = data.sortTimeSlots();
            const DaysOfWeekActive = [];
            timeSlots.forEach(timeSlot => {
                if (timeSlot.slots.size) {
                    DaysOfWeekActive.push(timeSlot.getShortCode());
                }
            });
            return DaysOfWeekActive.join(' ');
        },
        Cell: props => {
            const { original: location, column } = props;
            const { t } = column.getHeaderProps();
            const sortedTimeSlots = location.initializeWeekSlots();
            return (
                <LocationSlotsSummary
                    timeSlots={sortedTimeSlots.timeSlots}
                    title={t('workingHours')}
                />
            );
        }
    },
    {
        headerTextId: 'outletTable.currentRiderId',
        fieldId: 'historicalData.currentRiderId',
        accessor: 'historicalData.currentRiderId',
        filterable: false,
        sortable: false,
        minWidth: 120
    },
    {
        headerTextId: 'outletTable.currentPjpName',
        accessor: 'historicalData.currentBeatId',
        minWidth: 120,
        infixSearchable: true
    },
    {
        headerTextId: 'outletTable.currentPjpType',
        accessor: 'historicalData.currentPjpType',
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { entitySettings } = column.getHeaderProps();
            return CustomColumnHelpers.getOptionsByKeyName(
                entitySettings,
                'outletSettings',
                'currentPjpType'
            );
        },
        sortable: false,
        minWidth: 120
    },
    {
        headerTextId: 'outletTable.transactionDuration',
        id: 'transactionDuration',
        property: 'transactionDuration',
        dataType: CellDataTypes.HumanizedTime,
        accessor: 'transactionDuration',
        filterable: false,
        minWidth: 120,
        timeUnits: ['h', 'm', 's']
    },
    {
        headerTextId: 'outletTable.geocodeVerified',
        id: 'verified',
        accessor: 'locationInfo.location.geocodingMetadata.verified',
        filterable: false,
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            return [
                {
                    value: true,
                    label: t('outletTable.verificationStatus.verified')
                },
                {
                    value: false,
                    label: t('outletTable.verificationStatus.unverified')
                }
            ];
        },
        Cell: props => {
            const {
                original: outlet,
                tdProps: {
                    rest: { theme, toggleVerification }
                }
            } = props;
            return (
                <VerifiedButton
                    theme={theme}
                    location={outlet.locationInfo.location}
                    toggleVerification={toggleVerification}
                />
            );
        },
        sortable: false,
        minWidth: 120
    },
    {
        headerTextId: 'outletTable.revenueGenerated',
        accessor: 'historicalData.recentRevenue.amount.amount',
        sortable: false,
        filterable: false,
        minWidth: 215
    },
    {
        headerTextId: 'outletTable.skuSales',
        accessor: 'historicalData.averageSkusSold',
        sortable: false,
        filterable: false,
        minWidth: 120
    },
    {
        headerTextId: 'outletTable.shouldKeep',
        id: 'shouldKeep',
        accessor: 'historicalData.shouldKeep',
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            return [
                { value: true, label: t('outletTable.keepText') },
                {
                    value: false,
                    label: t('outletTable.removeText')
                }
            ];
        },
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const {
                historicalData: { shouldKeep }
            } = original;
            return shouldKeep
                ? t('outletTable.keepText')
                : t('outletTable.removeText');
        },
        sortable: false,
        minWidth: 120
    },
    {
        headerTextId: 'outletTable.serviceMix',
        accessor: 'serviceMix',
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { entitySettings } = column.getHeaderProps();
            const options = CustomColumnHelpers.getOptionsByKeyName(
                entitySettings,
                'outletSettings',
                'serviceMix'
            );
            return options;
        },
        sortable: false,
        minWidth: 120
    },
    {
        headerTextId: 'outletTable.saleMadeRecently',
        id: 'saleMadeRecently',
        accessor: 'historicalData.saleMadeRecently',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const {
                historicalData: { saleMadeRecently }
            } = original;
            return saleMadeRecently
                ? t('outletTable.saleMadeRecentlyText')
                : t('outletTable.noSaleMadeRecentlyText');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('outletTable.saleMadeRecentlyText') },
                { value: false, label: t('outletTable.noSaleMadeRecentlyText') }
            ];
            return optionsForSelect;
        },
        minWidth: 165
    },
    {
        headerTextId: 'outletTable.duplicateParty',
        id: 'duplicateParty',
        accessor: 'historicalData.isDuplicateParty',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const {
                historicalData: { isDuplicateParty }
            } = original;
            return isDuplicateParty
                ? t('outletTable.isDuplicatePartyText')
                : t('outletTable.isNotDuplicatePartyText');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('outletTable.isDuplicatePartyText') },
                {
                    value: false,
                    label: t('outletTable.isNotDuplicatePartyText')
                }
            ];
            return optionsForSelect;
        },
        minWidth: 220
    },
    {
        headerTextId: 'outletTable.isNew',
        id: 'isNew',
        accessor: 'historicalData.isNew',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const {
                historicalData: { isNew }
            } = original;
            return isNew
                ? t('outletTable.isNewText')
                : t('outletTable.isNotNewText');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('outletTable.isNewText') },
                { value: false, label: t('outletTable.isNotNewText') }
            ];
            return optionsForSelect;
        }
    },
    {
        id: 'currentPjpDayInfo',
        headerTextId: 'outletForm.currentPjpDayInfo',
        accessor: 'historicalData.currentPjpDayInfo.dayOfWeek',
        minWidth: 150,
        filterable: false,
        sortable: false,
        Cell: ({ value }) => {
            return (
                <MandatoryDaysSummary mandatoryDays={value ? [value] : []} />
            );
        }
    },
    {
        id: 'holidays',
        headerTextId: 'outletForm.holidays',
        accessor: 'holidays',
        minWidth: 150,
        filterable: false,
        sortable: false,
        Cell: props => {
            const {
                original: { holidays },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return (
                <DivWithPopover
                    theme={theme}
                    preferPlace={PopoverPlaceEnum.Right}
                    invertPopover={false}
                    triggerPopoverOn={PopoverTriggerEnum.Hover}
                    popOverContent={
                        <HolidaySelector
                            t={t}
                            disableClick
                            holidays={holidays || []}
                        />
                    }
                >
                    Holidays
                </DivWithPopover>
            );
        }
    },
    {
        id: 'currentPJPWeek',
        headerTextId: 'outletForm.currentPJPWeek',
        accessor: 'historicalData.currentPjpDayInfo.week',
        filterable: false,
        sortable: false
    },
    {
        id: 'isFrozenToRider',
        headerTextId: 'outletForm.isFrozenToRider',
        accessor: 'historicalData.isFrozenToRider',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const {
                historicalData: { isFrozenToRider }
            } = original;
            return isFrozenToRider
                ? t('outletTable.isFrozenText')
                : t('outletTable.isNotFrozenText');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('outletTable.isFrozenText') },
                { value: false, label: t('outletTable.isNotFrozenText') }
            ];
            return optionsForSelect;
        }
    },
    {
        id: 'frozenRiderId',
        headerTextId: 'masterOutletProperties.frozenRiderId',
        accessor: 'historicalData.frozenRiderId'
    },
    {
        id: 'isFrozenToBeat',
        headerTextId: 'outletForm.isFrozenToBeat',
        accessor: 'historicalData.isFrozenToBeat',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const {
                historicalData: { isFrozenToBeat }
            } = original;
            return isFrozenToBeat
                ? t('outletTable.isFrozenText')
                : t('outletTable.isNotFrozenText');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('outletTable.isFrozenText') },
                { value: false, label: t('outletTable.isNotFrozenText') }
            ];
            return optionsForSelect;
        }
    },
    {
        id: 'frozenBeat',
        headerTextId: 'masterOutletProperties.frozenBeat',
        accessor: 'historicalData.frozenTourId'
    },
    {
        id: 'serviceBySameRider',
        headerTextId: 'outletForm.serviceBySameRider',
        accessor: 'historicalData.serviceBySameRider',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const {
                historicalData: { serviceBySameRider }
            } = original;
            return serviceBySameRider
                ? t('outletTable.isFrozenText')
                : t('outletTable.isNotFrozenText');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('outletTable.isFrozenText') },
                { value: false, label: t('outletTable.isNotFrozenText') }
            ];
            return optionsForSelect;
        }
    },
    {
        id: 'beatDuration',
        headerTextId: 'outletForm.duration',
        accessor: 'visitFrequencyInfo.duration',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const {
                visitFrequencyInfo: { duration }
            } = original;
            const week = duration / 7;
            const weekLabel =
                week === 1
                    ? t('outletTable.week')
                    : t('outletTable.week_plural');
            return `${week} ${weekLabel}`;
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            return getWeekValues(t);
        }
    },
    {
        id: 'beatRepeatCount',
        headerTextId: 'outletForm.repeatCount',
        accessor: 'visitFrequencyInfo.repeatCount'
    },
    {
        id: 'spaceVisitsEvenly',
        headerTextId: 'outletForm.spaceVisitsEvenly',
        accessor: 'visitFrequencyInfo.spaceVisitsEvenly',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const {
                visitFrequencyInfo: { spaceVisitsEvenly }
            } = original;
            return spaceVisitsEvenly
                ? t('outletTable.isFrozenText')
                : t('outletTable.isNotFrozenText');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('outletTable.isFrozenText') },
                { value: false, label: t('outletTable.isNotFrozenText') }
            ];
            return optionsForSelect;
        }
    },
    {
        id: 'zoneId',
        headerTextId: 'masterOutletProperties.zoneId',
        accessor: 'zoneId',
        infixSearchable: true,
        sortable: false
    },
    {
        id: 'mandatoryDays',
        headerTextId: 'outletTable.mandatoryDays',
        accessor: 'mandatoryDays',
        minWidth: 150,
        filterable: false,
        sortable: false,
        Cell: ({ value }) => {
            return <MandatoryDaysSummary mandatoryDays={value || []} />;
        }
    },
    {
        headerTextId: 'outletTable.volume',
        accessor: 'averageVolume',
        sortable: true,
        filterable: true,
        minWidth: 100
    },
    {
        headerTextId: 'outletTable.territoryId',
        accessor: 'territoryId',
        sortable: true,
        filterable: true,
        minWidth: 210
    },
    {
        headerTextId: 'outletTable.territoryPlanId',
        accessor: 'territoryPlanId',
        sortable: true,
        filterable: true,
        minWidth: 210
    },
    {
        headerTextId: 'outletTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                original?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'outletTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'outletTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                original?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'outletTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type LocationsDataType = {
    locations: Array<MasterLocation>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    teams: Map<string, Team>,
    skills: Array<Skill>,
    fetchEntities: ({ clientId: string }, Object) => void,
    geocodeLocationsAction: Function,
    geocodeLocationsSuccessAction: Function,
    disableEntity: ({ clientId: string, locationId: string }) => Promise<*>,
    toggleVerification: (
        { clientId: string, locationId: string },
        MasterLocation,
        options?: Object
    ) => Promise<*>,
    clearData: () => void,
    selectedRows: Array<string>,
    data: ?LocationsDataType,
    isAddLocationActive: boolean,
    entityIdToEdit: ?string,
    isBulkEditLocationActive: boolean,
    isLoading: boolean,
    location: Location,
    history: RouterHistory,
    theme: Object,
    t: (string, ?Object) => string,
    entitySettings: ?EntitySettings,
    tableSettings: ?Array<Object>,
    updateLocation: (
        {
            clientId: string,
            locationId: string
        },
        MasterLocation,
        MasterLocation,
        options?: Object
    ) => Promise<*>,
    showSuccessToast: ($Shape<ToastType>) => void,
    showErrorToast: ($Shape<ToastType>) => void,
    saveTableSettings: (string, Array<Object>) => void,
    toggleRowSelection: string => void,
    isGlobalPersonnel: boolean,
    verifyEntityAction: Function,
    editEntityLatLngAction: Function,
    validateGeocodesAction: Function,
    clearGeocodeValidationDataAction: Function,
    getTdProps?: (state: Object, rowInfo: Object) => {}
};

type State = {
    geocodingEditActive?: boolean,
    showSlotSelector?: boolean
};

const outletMasterContainerFactory = ({
    EntityEditViewContainer = SingleOutletEditContainer,
    EntityImportContainer = OutletMasterImportContainer,
    EntityExportContainer = MasterExportContainer,
    COLUMNS = DEFAULT_COLUMNS,
    options = {
        entityTableId: 'OutletMaster',
        operations: {
            edit: RoleOperationEnum.UPDATE_OUTLETS,
            add: RoleOperationEnum.UPDATE_OUTLETS,
            export: RoleOperationEnum.EXPORT_OUTLETS
        }
    },
    ActionsBar,
    BulkViewEmptyState,
    fetchEntities = OutletActions.fetchActiveEntities
}) => {
    const OutletMaster = entityMasterFactory({
        EntityEditViewContainer,
        EntityImportContainer,
        EntityExportContainer,
        COLUMNS,
        options,
        ActionsBar,
        BulkViewEmptyState
    });
    class OutletMasterContainer extends React.PureComponent<Props, State> {
        prevRequestParams: Object;
        columns: Array<Object>;
        masterContainer: ?React.ElementRef<any>;

        constructor(props) {
            super(props);
            this.state = {
                geocodingEditActive: false
            };
        }

        componentDidMount() {
            const { fetchSalesCategories, clientId } = this.props;
            const defaultActiveFilter = new FilterListBuilder()
                .add(
                    new FilterBuilder()
                        .withName('status')
                        .withOperation(FilterOperation.Equals)
                        .withValue('ACTIVE')
                )
                .build();

            const body = { filters: defaultActiveFilter };
            if (clientId) {
                fetchSalesCategories({ clientId }, body);
            }
        }

        onColumnsChange = (newColumns: Array<Object>) => {
            this.columns = newColumns;
            const localPrefsData = newColumns.map(column => {
                return {
                    id: column.id || column.accessor,
                    show: column.show
                };
            });
            this.props.saveTableSettings(ENTITY_TABLE_ID, localPrefsData);
        };

        getTdProps = (state, rowInfo) => {
            if (!rowInfo) {
                return {};
            }
            const { original } = rowInfo;
            const { id } = original;
            const {
                teams,
                skills,
                toggleVerification,
                selectedRows,
                toggleRowSelection,
                isGlobalPersonnel,
                t,
                getTdProps
            } = this.props;
            const isChecked = selectedRows.indexOf(id) >= 0;
            const parentTdProps = getTdProps?.(state, rowInfo) || {};
            return {
                onCheckboxClick: () => {
                    toggleRowSelection(id);
                },
                isChecked,
                className: classnames({ [theme.selectedRow]: isChecked }),
                theme,
                t,
                teams,
                skills,
                toggleVerification,
                isGlobalPersonnel,
                ...parentTdProps
            };
        };

        handleBulkLocationGeocodeDismiss = (changed: boolean = false) => {
            this.setState({ geocodingEditActive: false });
            if (changed) {
                this.masterContainer && this.masterContainer.reloadData();
            }
        };

        handleGeocodingEditClick = () => {
            this.setState({ geocodingEditActive: true });
        };

        toggleSlotSelectorVisiblity = () => {
            const { selectedRows } = this.props;
            const { showSlotSelector } = this.state;
            if (!showSlotSelector) {
                if (!selectedRows.length) {
                    return;
                }
                this.setState({ showSlotSelector: true });
                return;
            }
            this.setState({ showSlotSelector: false });
        };

        getAllSelectedLocations = () => {
            const { data, selectedRows } = this.props;
            if (!data) {
                throw new Error("can't find locationsData");
            }
            return data.entities.filter(location => {
                return selectedRows.indexOf(location.id) > -1;
            });
        };

        getAdditionalTableProps = () => {
            const { teams } = this.props;
            return {
                teams
            };
        };

        getMasterContainerRef = ref => {
            this.masterContainer = ref;
        };

        @memoizedImmutable()
        getLocationsToGeocode(selectedRows, outlets) {
            const selectedOutlets = outlets.filter(outletEntity =>
                selectedRows.includes(outletEntity.id)
            );
            //get Unique locations from selectedOutlets
            const uniqueLocationsMap = selectedOutlets.reduce((res, outlet) => {
                //ESLINT fix
                let updatedRes = res;
                const {
                    locationInfo: { location }
                } = outlet;
                if (location && location.id) {
                    if (updatedRes.get(location.code || location.id)) {
                        updatedRes = updatedRes.update(
                            location.code || location.id,
                            data =>
                                data.setMetadata(
                                    data.metadata.push(
                                        outlet.code ? outlet.code : outlet.id
                                    )
                                )
                        );
                    } else {
                        let updLocationWithMetadata = LocationWithMetadata.fromJS(
                            {
                                metadata: [
                                    outlet.code ? outlet.code : outlet.id
                                ]
                            }
                        );
                        updLocationWithMetadata = updLocationWithMetadata.setLocation(
                            location
                        );
                        updatedRes = updatedRes.set(
                            location.code || location.id,
                            updLocationWithMetadata
                        );
                    }
                }
                return updatedRes;
            }, Immutable.Map({}));
            return uniqueLocationsMap.valueSeq();
        }

        getIsRowLoading = (original: Object, dataType: string) => {
            if (dataType === CellDataTypes.LatLng) {
                return (
                    original.getIn(['locationInfo', 'location', 'geocoded']) ===
                    false
                );
            }
        };

        render() {
            const {
                selectedRows,
                geocodeLocationsAction,
                geocodeLocationsSuccessAction,
                clientId,
                data,
                customActions,
                fetchOptions,
                t,
                pollLocationSearch,
                verifyEntityAction,
                editEntityLatLngAction,
                validateGeocodesAction,
                clearGeocodeValidationDataAction
            } = this.props;
            const { geocodingEditActive } = this.state;
            let enableGeocodingEdit = false;
            let geocodeEditLocations = Immutable.Map();
            if (selectedRows.length > 0) {
                enableGeocodingEdit = true;
                if (data) {
                    geocodeEditLocations = this.getLocationsToGeocode(
                        selectedRows,
                        data.entities
                    );
                }
            }
            const customOutletActions = {
                ...(customActions || {}),
                onLocationEdit: enableGeocodingEdit
                    ? this.handleGeocodingEditClick
                    : null
            };
            const outletFetchOptions = {
                clientId,
                include: IncludeTypeEnums.SalesCategory,
                ...(fetchOptions || {})
            };

            return (
                <OutletMaster
                    {...this.props}
                    refProvider={this.getMasterContainerRef}
                    getIsRowLoading={this.getIsRowLoading}
                    customActions={customOutletActions}
                    emptyStateImageSrc="/img/location-master-empty.png"
                    entityNameId={ENTITY_NAME_ID}
                    entityExportId={ENTITY_EXPORT_ID}
                    entityId={'outletId'}
                    entitySettingsName={'outletSettings'}
                    fetchOptions={outletFetchOptions}
                    getTdProps={this.getTdProps}
                    hasFreezableColumns
                    frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                    pinTooltip={t('pinTooltip')}
                    unpinTooltip={t('unpinTooltip')}
                    getAdditionalTableProps={this.getAdditionalTableProps}
                >
                    {geocodingEditActive ? (
                        <BulkGeocodeEditContainer
                            entity={ENTITY_TABLE_ID}
                            verifyEntityAction={verifyEntityAction}
                            editEntityLatLngAction={editEntityLatLngAction}
                            validateGeocodesAction={validateGeocodesAction}
                            clearGeocodeValidationDataAction={
                                clearGeocodeValidationDataAction
                            }
                            geocodeEditLocations={geocodeEditLocations}
                            geocodeLocationsAction={geocodeLocationsAction}
                            geocodeLocationsSuccessAction={
                                geocodeLocationsSuccessAction
                            }
                            onDismiss={this.handleBulkLocationGeocodeDismiss}
                            pollLocationSearch={pollLocationSearch}
                        />
                    ) : (
                        []
                    )}
                </OutletMaster>
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const clientId = clientSelector(state, ownProps);
        const isLoading = OutletSelectors.isEntitiesLoading(state, ownProps);
        const data = OutletSelectors.getEntities(state, ownProps);
        const skills = SkillSelectors.getEntities(state, ownProps);
        const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
        const isImportEntityActive = isImportEntityActiveSelector(
            state,
            ownProps
        );
        const isExportEntityActive = isExportEntityActiveSelector(
            state,
            ownProps
        );
        const entityIdToEdit = editEntityIdSelector(state, ownProps);
        const teams = teamsSelector(state, ownProps);
        const entitySettings = getEntitySettings(state, ownProps);
        const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
        const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);

        return {
            clientId,
            teams,
            skills,
            isLoading,
            data,
            isAddEntityActive,
            entityIdToEdit,
            entitySettings,
            tableSettings,
            isExportEntityActive,
            isImportEntityActive,
            isGlobalPersonnel
        };
    };

    const withRedux = connect(mapStateToProps, {
        fetchEntities,
        fetchSalesCategories: SalesCategoriesActions.fetchSalesCategories,
        bulkDisableEntity: OutletActions.bulkDisableEntity,
        pollLocationSearch: OutletActions.pollLocationSearch,
        updateOutlet: OutletActions.patchEntity,
        geocodeLocationsAction: OutletActions.bulkGeocode,
        geocodeLocationsSuccessAction: OutletActions.bulkGeocodeSuccess,
        toggleVerification: OutletActions.toggleLocationVerification,
        disableEntity: OutletActions.disableEntity,
        clearData: OutletActions.clearData,
        showSuccessToast: ToastActions.showSuccessToast,
        showErrorToast: ToastActions.showErrorToast,
        saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
        showToastAction: ToastActions.showToast,
        updateToastAction: ToastActions.updateToast,
        hideToastAction: ToastActions.hideToast,
        verifyEntityAction: OutletLocationActions.verifyOutletLocation,
        editEntityLatLngAction: OutletLocationActions.editOutletLocationLatLng,
        validateGeocodesAction:
            OutletLocationActions.validateOutletLocationGeocodes,
        clearGeocodeValidationDataAction:
            OutletLocationActions.clearGeocodeValidationData
    });

    const enhance = compose(
        themr(null, theme),
        withRouter,
        withRedux,
        translate('entityManagement'),
        MasterContainerHOC
    );
    return enhance(OutletMasterContainer);
};

const ThemedConnectedOutletMasterContainer = outletMasterContainerFactory({});
export {
    outletMasterContainerFactory,
    ThemedConnectedOutletMasterContainer as OutletMasterContainer
};
export default ThemedConnectedOutletMasterContainer;
